import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Select,
  Tag,
  Space,
  Descriptions,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Empty,
  Button,
  Tabs,
  Image,
  Tooltip,
  Modal,
  Form,
  Input,
} from 'antd';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Icons from '@/components/third-party/Icons';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import {
  get_completed_batches_for_cleaning,
  get_equipment_list_for_cleaning,
  get_cleaning_logs_by_batch,
  get_cleaning_logs_by_equipment,
  start_machine_cleaning,
  complete_machine_cleaning,
  approve_machine_cleaning,
  export_cleaning_logs,
} from '@/utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const CleaningLogs = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // Dark mode colors
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const nestedBgColor = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const highlightBgColor = isDark ? '#1e3a5f' : '#eff6ff';
  const highlightBorderColor = isDark ? '#1e40af' : '#bfdbfe';
  const verifiedBgColor = isDark ? '#14532d' : '#f0fdf4';
  const verifiedBorderColor = isDark ? '#166534' : '#bbf7d0';
  const approvedBgColor = isDark ? '#1e3a5f' : '#eff6ff';
  const approvedBorderColor = isDark ? '#1e40af' : '#bfdbfe';

  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('batch'); // 'batch' or 'equipment'
  const [batchList, setBatchList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [logData, setLogData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [completeForm] = Form.useForm();
  const [approveForm] = Form.useForm();

  // Mock batch list
  const mockBatchList = [
    { batch_number: 'BT-2025-0001', recipe_name: 'Aspirin Tablet 500mg', status: 'In Progress' },
    { batch_number: 'BT-2025-0002', recipe_name: 'Paracetamol Syrup', status: 'Completed' },
    { batch_number: 'BT-2025-0003', recipe_name: 'Ibuprofen Tablet 400mg', status: 'Completed' },
  ];

  useEffect(() => {
    fetchBatchList();
    fetchEquipmentList();
  }, []);

  const fetchBatchList = async () => {
    setLoading(true);
    try {
      get_completed_batches_for_cleaning((response) => {
        if (response?.data?.status === 'success') {
          setBatchList(response.data.data || []);
        } else {
          console.error('Error fetching batch list:', response?.data?.message);
          toast.error(response?.data?.message || 'Failed to fetch batch list');
          setBatchList([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching batch list:', error);
      toast.error('Failed to fetch batch list');
      setBatchList([]);
      setLoading(false);
    }
  };

  const fetchEquipmentList = async () => {
    setLoading(true);
    try {
      get_equipment_list_for_cleaning((response) => {
        if (response?.data?.status === 'success') {
          setEquipmentList(response.data.data || []);
        } else {
          console.error('Error fetching equipment list:', response?.data?.message);
          toast.error(response?.data?.message || 'Failed to fetch equipment list');
          setEquipmentList([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching equipment list:', error);
      toast.error('Failed to fetch equipment list');
      setEquipmentList([]);
      setLoading(false);
    }
  };

  const fetchLogData = async (type, value) => {
    setLoading(true);
    try {
      if (type === 'batch') {
        get_cleaning_logs_by_batch(value, (response) => {
          if (response?.data?.status === 'success') {
            // Backend returns batch_info and cleaning_activities directly
            const data = {
              batch_number: response.data.batch_info?.batch_number,
              recipe_name: response.data.batch_info?.recipe_name,
              product_code: response.data.batch_info?.product_code,
              cleaning_activities: response.data.cleaning_activities || [],
            };
            setLogData(data);
          } else {
            console.error('Error fetching log data:', response?.data?.message);
            toast.error(response?.data?.message || 'Failed to fetch cleaning logs');
            setLogData(null);
          }
          setLoading(false);
        });
      } else if (type === 'equipment') {
        get_cleaning_logs_by_equipment(value, (response) => {
          if (response?.data?.status === 'success') {
            // Backend returns equipment_info and cleaning_activities directly
            const data = {
              equipment_id: response.data.equipment_info?.equipment_id,
              equipment_name: response.data.equipment_info?.equipment_name,
              equipment_code: response.data.equipment_info?.equipment_code,
              equipment_type: response.data.equipment_info?.equipment_type,
              cleaning_activities: response.data.cleaning_activities || [],
            };
            setLogData(data);
          } else {
            console.error('Error fetching log data:', response?.data?.message);
            toast.error(response?.data?.message || 'Failed to fetch cleaning logs');
            setLogData(null);
          }
          setLoading(false);
        });
      }
    } catch (error) {
      console.error('Error fetching log data:', error);
      setLoading(false);
      toast.error('Failed to fetch cleaning logs');
    }
  };

  const handleBatchChange = (batchNumber) => {
    setSelectedBatch(batchNumber);
    setSelectedEquipment(null);
    if (batchNumber) {
      fetchLogData('batch', batchNumber);
    } else {
      setLogData(null);
    }
  };

  const handleEquipmentChange = (equipmentId) => {
    setSelectedEquipment(equipmentId);
    setSelectedBatch(null);
    if (equipmentId) {
      fetchLogData('equipment', equipmentId);
    } else {
      setLogData(null);
    }
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    setSelectedBatch(null);
    setSelectedEquipment(null);
    setLogData(null);
  };

  // Action Handlers
  const handleStartCleaning = (record) => {
    setActionLoading(true);
    const data = {
      remarks: 'Starting cleaning process',
    };
    start_machine_cleaning(record.id, data, (response) => {
      if (response?.status === 'success') {
        toast.success('Cleaning started successfully');
        // Refresh the log data
        if (selectedBatch) {
          fetchLogData('batch', selectedBatch);
        } else if (selectedEquipment) {
          fetchLogData('equipment', selectedEquipment);
        }
      } else {
        toast.error(response?.message || 'Failed to start cleaning');
      }
      setActionLoading(false);
    });
  };

  const handleCompleteCleaningClick = (record) => {
    setSelectedRecord(record);
    setCompleteModalVisible(true);
    completeForm.resetFields();
  };

  const handleCompleteCleaningSubmit = () => {
    completeForm
      .validateFields()
      .then((values) => {
        setActionLoading(true);
        const data = {
          verification_method: values.verification_method || 'VISUAL',
          verification_results: values.verification_results,
          cleaning_agents_used: values.cleaning_agents_used,
          remarks: values.remarks,
        };
        complete_machine_cleaning(selectedRecord.id, data, (response) => {
          if (response?.status === 'success') {
            toast.success('Cleaning completed and sent for approval');
            setCompleteModalVisible(false);
            // Refresh the log data
            if (selectedBatch) {
              fetchLogData('batch', selectedBatch);
            } else if (selectedEquipment) {
              fetchLogData('equipment', selectedEquipment);
            }
          } else {
            toast.error(response?.message || 'Failed to complete cleaning');
          }
          setActionLoading(false);
        });
      })
      .catch((errorInfo) => {
        // Form validation failed
        console.log('Validation Failed:', errorInfo);
        toast.error('Please fill in all required fields');
      });
  };

  const handleApproveRejectClick = (record, action) => {
    setSelectedRecord({ ...record, action });
    setApproveModalVisible(true);
    approveForm.resetFields();
  };

  const handleApproveRejectSubmit = () => {
    approveForm
      .validateFields()
      .then((values) => {
        setActionLoading(true);
        const data = {
          approval_status: selectedRecord.action === 'approve' ? 'APPROVED' : 'REJECTED',
          comments: values.comments,
        };
        approve_machine_cleaning(selectedRecord.id, data, (response) => {
          if (response?.status === 'success') {
            toast.success(
              selectedRecord.action === 'approve'
                ? 'Cleaning approved. Machine is now available.'
                : 'Cleaning rejected. Please re-clean.'
            );
            setApproveModalVisible(false);
            // Refresh the log data
            if (selectedBatch) {
              fetchLogData('batch', selectedBatch);
            } else if (selectedEquipment) {
              fetchLogData('equipment', selectedEquipment);
            }
          } else {
            toast.error(response?.message || 'Failed to process approval');
          }
          setActionLoading(false);
        });
      })
      .catch((errorInfo) => {
        // Form validation failed
        console.log('Validation Failed:', errorInfo);
        toast.error('Please fill in all required fields');
      });
  };

  const getStatusColor = (status) => {
    const colors = {
      COMPLETED: 'success',
      APPROVED: 'success',
      IN_PROGRESS: 'processing',
      PENDING: 'warning',
      REJECTED: 'error',
      Completed: 'success',
      'In Progress': 'processing',
      Pending: 'warning',
      Failed: 'error',
      Scheduled: 'default',
    };
    return colors[status] || 'default';
  };

  const handleExport = () => {
    toast.info('Export functionality - API integration needed');
  };

  // Cleaning Activity Columns
  const activityColumns = [
    {
      title: 'Equipment Name',
      dataIndex: 'equipment_name',
      key: 'equipment_name',
      width: 180,
      render: (text, record) => (
        <div>
          <Text strong className="text-blue-600">
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.equipment_code || record.equipment_id || ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Equipment Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      width: 130,
      render: (type) => (
        <Tooltip title={type}>
          <Tag color="blue">
            {type?.length > 16 ? `${type?.slice(0, 16)}...` : type ? type : 'N/A'}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Area',
      dataIndex: 'area',
      key: 'area',
      width: 150,
    },
    {
      title: 'Cleaning Type',
      dataIndex: 'cleaning_type',
      key: 'cleaning_type',
      width: 180,
      render: (type) => {
        const typeName = type?.name || type || 'Standard';
        const category = type?.category || '';
        return (
          <Tooltip title={typeName}>
            <Tag color="purple">{typeName}</Tag>
            {category && (
              <div>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {category}
                </Text>
              </div>
            )}
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      width: 120,
      render: (duration) => (duration ? `${duration} min` : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (text, record) => (
        <Space size="small" direction="vertical">
          {record.status === 'PENDING' && (
            <Button
              type="primary"
              size="small"
              icon={<Icons.PlayCircle size={20} />}
              onClick={() => handleStartCleaning(record)}
              loading={actionLoading}
              style={{ width: '100%' }}
            >
              Start
            </Button>
          )}
          {record.status === 'IN_PROGRESS' && (
            <Button
              type="primary"
              size="small"
              icon={<Icons.CheckCircle size={20} />}
              onClick={() => handleCompleteCleaningClick(record)}
              loading={actionLoading}
              style={{ width: '100%' }}
            >
              Complete
            </Button>
          )}
          {record.status === 'COMPLETED' && (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<Icons.Check size={20} />}
                onClick={() => handleApproveRejectClick(record, 'approve')}
                loading={actionLoading}
                style={{ background: 'green', borderColor: 'green' }}
              >
                Approve
              </Button>
              <Button
                type="primary"
                danger
                size="small"
                icon={<Icons.X size={20} />}
                onClick={() => handleApproveRejectClick(record, 'reject')}
                loading={actionLoading}
              >
                Reject
              </Button>
            </Space>
          )}
          {(record.status === 'APPROVED' || record.status === 'REJECTED') && (
            <Tag color={record.status === 'APPROVED' ? 'green' : 'red'}>
              {record.status === 'APPROVED' ? 'Approved' : 'Rejected'}
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  // Cleaning Steps Columns
  const stepsColumns = [
    {
      title: 'Step No',
      dataIndex: 'step_number',
      key: 'step_number',
      width: 80,
      render: (num) => <Tag color="cyan">{num}</Tag>,
    },
    {
      title: 'Step Name',
      dataIndex: 'step_name',
      key: 'step_name',
      width: 200,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by',
      key: 'performed_by',
      width: 150,
      render: (text) => (
        <Space>
          <Icons.User className="text-blue-500" size={16} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => (duration ? `${duration} min` : '-'),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  // Render Cleaning Steps Expand
  const renderCleaningSteps = (activity) => {
    if (!activity.cleaning_steps || activity.cleaning_steps.length === 0) {
      return (
        <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
          <Empty description="No cleaning steps recorded" />
        </div>
      );
    }

    return (
      <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
        <Title level={5} className="mb-3" style={{ color: textColor }}>
          Cleaning Steps
        </Title>

        {/* Cleaning Details */}
        <Card className="mb-4" size="small" style={{ backgroundColor: highlightBgColor, borderColor: highlightBorderColor }}>
          <Descriptions bordered size="small" column={3}>
            <Descriptions.Item label="Previous Product">
              {activity.previous_product}
            </Descriptions.Item>
            <Descriptions.Item label="Previous Batch">{activity.previous_batch}</Descriptions.Item>
            <Descriptions.Item label="Cleaning Procedure">
              {activity.cleaning_procedure}
            </Descriptions.Item>
            <Descriptions.Item label="Next Product">{activity.next_product}</Descriptions.Item>
            <Descriptions.Item label="Next Batch">{activity.next_batch}</Descriptions.Item>
            <Descriptions.Item label="Scheduled Date">
              {dayjs(activity.scheduled_date).format('DD-MMM-YYYY')}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Steps Table */}
        <Table
          columns={stepsColumns}
          dataSource={activity.cleaning_steps}
          pagination={false}
          size="small"
          rowKey="step_id"
          bordered
          expandable={{
            expandedRowRender: (step) => renderStepDetails(step),
            rowExpandable: (record) => record.cleaning_agent || record.attachments?.length > 0,
          }}
        />

        {/* Verification & Approval */}
        {(activity.verified_by || activity.approved_by) && (
          <>
            <Divider />
            <Row gutter={16}>
              {activity.verified_by && (
                <Col span={12}>
                  <Card size="small" style={{ backgroundColor: verifiedBgColor, borderColor: verifiedBorderColor }}>
                    <Space direction="vertical" size="small">
                      <Text strong style={{ color: textColor }}>
                        <Icons.CheckCircle className="text-green-600" size={16} /> Verified By
                      </Text>
                      <Text style={{ color: textColor }}>{activity.verified_by}</Text>
                      <Text style={{ fontSize: '12px', color: secondaryTextColor }}>
                        {dayjs(activity.verified_at).format('DD-MMM-YYYY HH:mm')}
                      </Text>
                    </Space>
                  </Card>
                </Col>
              )}
              {activity.approved_by && (
                <Col span={12}>
                  <Card size="small" style={{ backgroundColor: approvedBgColor, borderColor: approvedBorderColor }}>
                    <Space direction="vertical" size="small">
                      <Text strong style={{ color: textColor }}>
                        <Icons.CheckCircle className="text-blue-600" size={16} /> Approved By
                      </Text>
                      <Text style={{ color: textColor }}>{activity.approved_by}</Text>
                      <Text style={{ fontSize: '12px', color: secondaryTextColor }}>
                        {dayjs(activity.approved_at).format('DD-MMM-YYYY HH:mm')}
                      </Text>
                    </Space>
                  </Card>
                </Col>
              )}
            </Row>
          </>
        )}
      </div>
    );
  };

  // Render Step Details (Cleaning Agent, Water, Attachments)
  const renderStepDetails = (step) => {
    return (
      <div className="p-4" style={{ backgroundColor: cardBgColor }}>
        <Row gutter={16}>
          {/* Cleaning Agent Details */}
          {step.cleaning_agent && (
            <Col span={12}>
              <Descriptions title="Cleaning Agent Details" bordered size="small" column={1}>
                <Descriptions.Item label="Cleaning Agent">{step.cleaning_agent}</Descriptions.Item>
                {step.concentration && (
                  <Descriptions.Item label="Concentration">{step.concentration}</Descriptions.Item>
                )}
                {step.temperature && (
                  <Descriptions.Item label="Temperature">
                    {step.temperature} {step.temperature_uom}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          )}

          {/* Water Details */}
          {step.water_type && (
            <Col span={12}>
              <Descriptions title="Rinse Water Details" bordered size="small" column={1}>
                <Descriptions.Item label="Water Type">{step.water_type}</Descriptions.Item>
                {step.water_volume && (
                  <Descriptions.Item label="Volume">
                    {step.water_volume} {step.water_volume_uom}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          )}

          {/* Inspection Results */}
          {step.inspection_result && (
            <Col span={12}>
              <Descriptions title="Inspection Results" bordered size="small" column={1}>
                <Descriptions.Item label="Visual Inspection">
                  <Tag color={step.inspection_result === 'Pass' ? 'green' : 'red'}>
                    {step.inspection_result}
                  </Tag>
                </Descriptions.Item>
                {step.swab_test_result && (
                  <Descriptions.Item label="Swab Test">
                    <Tag color={step.swab_test_result === 'Negative' ? 'green' : 'red'}>
                      {step.swab_test_result}
                    </Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          )}

          {/* Attachments */}
          {step.attachments && step.attachments.length > 0 && (
            <Col span={24}>
              <Divider orientation="left">Attachments</Divider>
              <Space wrap>
                {step.attachments.map((attachment) => (
                  <Card
                    key={attachment.attachment_id}
                    size="small"
                    style={{ backgroundColor: cardBgColor }}
                  >
                    <Space direction="vertical" size="small">
                      {attachment.file_type === 'image' ? (
                        <Image
                          width={100}
                          src={attachment.file_path}
                          alt={attachment.file_name}
                          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                        />
                      ) : (
                        <Icons.FileText size={48} className="text-blue-500" />
                      )}
                      <Text style={{ fontSize: '12px' }}>{attachment.file_name}</Text>
                      <Button
                        type="link"
                        size="small"
                        icon={<Icons.Download size={14} />}
                        onClick={() =>
                          toast.info('Download functionality - API integration needed')
                        }
                      >
                        Download
                      </Button>
                    </Space>
                  </Card>
                ))}
              </Space>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  return (
    <div className="p-1.5 min-h-screen " style={{ backgroundColor: pageBgColor }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{ backgroundColor: cardBgColor }}
        title={
          <div className="flex items-center gap-2">
            <Icons.Droplet className="text-blue-600" size={24} />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('Digital Cleaning Logs')}
            </Title>
          </div>
        }
        extra={
          <Space size="middle">
            <Button
              type="default"
              icon={<Icons.Download size={16} />}
              onClick={handleExport}
              className="border-blue-500 text-blue-600"
            >
              Export
            </Button>
          </Space>
        }
      >
        {/* Filter Type Selection */}
        <Row gutter={16} className="mb-4">
          <Col xs={24} md={6}>
            <div className="mb-2">
              <Text strong style={{ color: textColor }}>Filter By:</Text>
            </div>
            <Select
              value={filterType}
              onChange={handleFilterTypeChange}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="batch">
                <Space>
                  <Icons.Package size={16} />
                  Batch Number
                </Space>
              </Option>
              <Option value="equipment">
                <Space>
                  <Icons.Cog size={16} />
                  Equipment ID
                </Space>
              </Option>
            </Select>
          </Col>

          {/* Batch/Equipment Selection */}
          <Col xs={24} md={12}>
            {filterType === 'batch' ? (
              <>
                <div className="mb-2">
                  <Text strong style={{ color: textColor }}>Select Batch Number:</Text>
                </div>
                <Select
                  placeholder="Select a batch to view cleaning logs"
                  value={selectedBatch}
                  onChange={handleBatchChange}
                  style={{ width: '100%' }}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  loading={loading}
                >{batchList.map((batch) => (
                    <Option key={batch.batch_number} value={batch.batch_number}>
                      <Space>
                        <Text strong>{batch.batch_number}</Text>
                        <Text type="secondary">-</Text>
                        <Text>{batch.recipe_name}</Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </>
            ) : (
              <>
                <div className="mb-2">
                  <Text strong style={{ color: textColor }}>Select Equipment:</Text>
                </div>
                <Select
                  placeholder="Select an equipment to view cleaning logs"
                  value={selectedEquipment}
                  onChange={handleEquipmentChange}
                  style={{ width: '100%' }}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  loading={loading}
                >
                  {equipmentList.map((equipment) => (
                    <Option key={equipment.equipment_id} value={equipment.equipment_id}>
                      <Space>
                        <Text strong>{equipment.equipment_id}</Text>
                        <Text type="secondary">-</Text>
                        <Text>{equipment.equipment_name}</Text>
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          {equipment.equipment_type}
                        </Tag>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </>
            )}
          </Col>
        </Row>

        {/* Filter Header Information */}
        {logData && (
          <>
            <Card className="mb-4" bordered style={{ backgroundColor: highlightBgColor, borderColor: highlightBorderColor }}>
              <Descriptions bordered size="small" column={3}>
                {logData.batch_number && (
                  <>
                    <Descriptions.Item label="Batch Number" span={1}>
                      <Text strong className="text-blue-600">
                        {logData.batch_number}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Recipe Name" span={1}>
                      {logData.recipe_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Product Code" span={1}>
                      {logData.product_code}
                    </Descriptions.Item>
                  </>
                )}
                {logData.equipment_id && (
                  <>
                    <Descriptions.Item label="Equipment ID" span={1}>
                      <Text strong className="text-blue-600">
                        {logData.equipment_id}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Equipment Name" span={2}>
                      {logData.equipment_name}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            <Divider orientation="left">
              <Title level={5} style={{ color: textColor }}>Cleaning Activities</Title>
            </Divider>

            {/* Cleaning Activities Table */}
            <Table
              columns={activityColumns}
              dataSource={logData.cleaning_activities}
              loading={loading}
              rowKey="id"
              pagination={false}
              bordered
              expandable={{
                expandedRowRender: renderCleaningSteps,
                rowExpandable: (record) => true,
              }}
              scroll={{ x: 1600 }}
            />
          </>
        )}

        {/* No Selection */}
        {!selectedBatch && !selectedEquipment && (
          <div className="text-center py-12">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  <Text className="text-lg" style={{ color: secondaryTextColor }}>
                    Please select a {filterType === 'batch' ? 'batch number' : 'equipment'} to view
                    cleaning logs
                  </Text>
                </span>
              }
            />
          </div>
        )}
      </Card>

      {/* Complete Cleaning Modal */}
      <Modal
        title="Complete Cleaning"
        open={completeModalVisible}
        onOk={handleCompleteCleaningSubmit}
        onCancel={() => setCompleteModalVisible(false)}
        confirmLoading={actionLoading}
        width={600}
      >
        <Form form={completeForm} layout="vertical">
          <Form.Item
            label="Verification Method"
            name="verification_method"
            initialValue="VISUAL"
            rules={[{ required: true, message: 'Please select verification method' }]}
          >
            <Select>
              <Option value="VISUAL">Visual Inspection</Option>
              <Option value="SWAB_TEST">Swab Test</Option>
              <Option value="RINSE_SAMPLE">Rinse Sample</Option>
              <Option value="ATP_TEST">ATP Test</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Verification Results"
            name="verification_results"
            rules={[{ required: true, message: 'Please enter verification results' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter verification results (e.g., 'Passed', 'All surfaces clean', etc.)"
            />
          </Form.Item>

          <Form.Item
            label="Cleaning Agents Used"
            name="cleaning_agents_used"
            rules={[{ required: true, message: 'Please enter cleaning agents used' }]}
          >
            <Input.TextArea
              rows={2}
              placeholder="Enter cleaning agents used (e.g., 'Detergent A, Sanitizer B')"
            />
          </Form.Item>

          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} placeholder="Additional remarks (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Approve/Reject Cleaning Modal */}
      <Modal
        title={selectedRecord?.action === 'approve' ? 'Approve Cleaning' : 'Reject Cleaning'}
        open={approveModalVisible}
        onOk={handleApproveRejectSubmit}
        onCancel={() => setApproveModalVisible(false)}
        confirmLoading={actionLoading}
        okText={selectedRecord?.action === 'approve' ? 'Approve' : 'Reject'}
        okButtonProps={{
          danger: selectedRecord?.action === 'reject',
          style:
            selectedRecord?.action === 'approve'
              ? { background: 'green', borderColor: 'green' }
              : {},
        }}
        width={500}
      >
        <Form form={approveForm} layout="vertical">
          <Form.Item
            label="Comments"
            name="comments"
            rules={[{ required: true, message: 'Please enter comments' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder={
                selectedRecord?.action === 'approve'
                  ? 'Enter approval comments (e.g., "Cleaning verified and approved")'
                  : 'Enter rejection reason (e.g., "Cleaning not satisfactory, please re-clean")'
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CleaningLogs;
